import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import HeroSection from "../sections/ResidentParcelDashboardSection/HeroSection/HeroSection";
import ServicesSection from "../sections/ResidentParcelDashboardSection/ServicesSection/ServicesSection";
import { AuthContext } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { pickuptabs } from "../sections/ResidentParcelDashboardSection/HeroSection/HS_StaticData";
import WelcomeModal from "../components/common/WelcomeModel";
import {
  fetchResidentDeliveriesAPI,
  fetchPreApprovedAPI,
} from "../services/resident_api";
import Header from "../components/Header/Header";

const ResidentParcelDashboard = () => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const role = user?.role || "primary";

  // ✅ State declarations
  const [residentName, setResidentName] = useState("");
  const [residentPhone, setResidentPhone] = useState("");
  const [activeTab, setActiveTab] = useState("Approval");
  const [tabLoaded, setTabLoaded] = useState(false);
  const [residentSessionData, setResidentSessionData] = useState([]);
  const [residentId, setResidentId] = useState(null);
  const [preApprovedList, setPreApprovedList] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // ─── Handle Tab Change (STABLE FUNCTION) ────────────────
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    localStorage.setItem("activeTab", tab);
  }, []);

  // ─── Persist Tab ────────────────
  useEffect(() => {
    const savedTab = localStorage.getItem("activeTab");

    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      localStorage.setItem("activeTab", location.state.activeTab);
    } else if (savedTab) {
      setActiveTab(savedTab);
    } else {
      setActiveTab("Approval");
      localStorage.setItem("activeTab", "Approval");
    }

    setTabLoaded(true);
  }, [location.state?.activeTab]);

  // ─── STABLE fetchResidentData Function ────────────────
  const fetchResidentData = useCallback(async () => {
    const resident_id = user?.id || user?.member_id;
    const phone =
      parseInt(user?.phone_number?.trim()) ||
      parseInt(user?.member_data?.phone_number?.trim());
    const name = user?.name || user?.member_data?.name;
    const locationName =
      user?.location?.trim() || user?.flats?.[0]?.location?.trim();
    const complex = (
      user?.building_name ||
      user?.buildingName ||
      user?.flats?.[0]?.building_name ||
      user?.flats?.[0]?.buildingName
    )?.trim();

    if (phone && locationName && complex && resident_id) {
      // Only update if values actually changed
      setResidentId((prev) => (prev === resident_id ? prev : resident_id));
      setResidentName((prev) => (prev === name ? prev : name || ""));
      setResidentPhone((prev) => (prev === phone ? prev : phone || ""));

      try {
        const deliveriesResponse = await fetchResidentDeliveriesAPI({
          phone,
          location: locationName,
          complex,
        });

        // Only update if data actually changed
        setResidentSessionData((prev) => {
          if (
            JSON.stringify(prev) === JSON.stringify(deliveriesResponse.data)
          ) {
            return prev;
          }
          return deliveriesResponse.data;
        });
      } catch {
        setResidentSessionData((prev) => (prev.length ? prev : []));
      }

      try {
        const preApprovedResponse = await fetchPreApprovedAPI(resident_id);

        // Only update if preApprovedList actually changed
        setPreApprovedList((prev) => {
          if (
            JSON.stringify(prev) === JSON.stringify(preApprovedResponse.data)
          ) {
            return prev;
          }
          return preApprovedResponse.data;
        });
      } catch {
        console.error("❌ Pre-approved fetch failed");
        setPreApprovedList((prev) => (prev.length ? prev : []));
      }
    }
  }, [user]); // Only depends on user

  // ─── Initial Load ────────────────
  useEffect(() => {
    if (user) {
      fetchResidentData();
    }
  }, [user, fetchResidentData]);

  // ─── Periodic Refresh ────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      if (user) fetchResidentData();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, fetchResidentData]);

  // ─── Show Modal for First-Time Users ────────────────
  useEffect(() => {
    const firstTime = localStorage.getItem("firstTimeLogin");
    if (firstTime === "true") {
      setShowModal(true);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    localStorage.setItem("firstTimeLogin", "false");
  }, []);

  // ─── MEMOIZED Props to Prevent Unnecessary Re-renders ────────────────
  const servicesSectionProps = useMemo(
    () => ({
      activeTab,
      residentSessionData,
      resident_id: residentId,
      preApprovedList,
      role,
      fetchResidentData,
      residentName,
      residentPhone,
    }),
    [
      activeTab,
      residentSessionData,
      residentId,
      preApprovedList,
      role,
      fetchResidentData,
      residentName,
      residentPhone,
    ]
  );

  return (
    <div>
      <Header />

      {tabLoaded && (
        <>
          <WelcomeModal isOpen={showModal} onClose={handleCloseModal} />

          <div className="fade-in">
            <HeroSection
              activeTab={activeTab}
              setActiveTab={handleTabChange}
              tabs={pickuptabs}
            />
            <br />
            <ServicesSection {...servicesSectionProps} />
          </div>
        </>
      )}
    </div>
  );
};

export default ResidentParcelDashboard;
